import { TFunction } from 'i18next'
import { z } from 'zod'

import type { ServiceSku } from '@/api/services'

import {
  SKU_COST_MAX,
  SKU_DURATION_MAX,
  SKU_NAME_MAX_LENGTH,
  SKU_QUANTITY_MAX,
} from '../services.constants'

type SkuT = TFunction<['organisation', 'common']>

interface SkuSchemaOptions {
  timed: boolean
}

const isWholeNumberInRange = (value: string, max: number) => {
  const number = Number(value)
  return /^\d+$/.test(value) && number >= 1 && number <= max
}

export const makeSkuFormSchema = (t: SkuT, { timed }: SkuSchemaOptions) =>
  z
    .object({
      active: z.boolean(),
      cost: z.string().trim().min(1, t('common:validation.required')),
      duration: z.string().trim(),
      name: z
        .string()
        .trim()
        .min(1, t('common:validation.required'))
        .max(
          SKU_NAME_MAX_LENGTH,
          t('organisation:servicesPage.validation.maxLength', { max: SKU_NAME_MAX_LENGTH }),
        ),
      quantity: z.string().trim(),
    })
    .superRefine((data, ctx) => {
      const cost = Number(data.cost)
      if (data.cost && (Number.isNaN(cost) || cost <= 0 || cost > SKU_COST_MAX)) {
        ctx.addIssue({
          code: 'custom',
          message: t('organisation:servicesPage.validation.costInvalid'),
          path: ['cost'],
        })
      }

      if (!timed) return

      // Note: without a duration the BE would name the training type "<service> undefined min"
      if (!data.duration) {
        ctx.addIssue({
          code: 'custom',
          message: t('organisation:servicesPage.validation.durationRequired'),
          path: ['duration'],
        })
      } else if (!isWholeNumberInRange(data.duration, SKU_DURATION_MAX)) {
        ctx.addIssue({
          code: 'custom',
          message: t('organisation:servicesPage.validation.durationInvalid', {
            max: SKU_DURATION_MAX,
          }),
          path: ['duration'],
        })
      }

      if (data.quantity && !isWholeNumberInRange(data.quantity, SKU_QUANTITY_MAX)) {
        ctx.addIssue({
          code: 'custom',
          message: t('organisation:servicesPage.validation.quantityInvalid', {
            max: SKU_QUANTITY_MAX,
          }),
          path: ['quantity'],
        })
      }
    })

export type SkuFormData = z.infer<ReturnType<typeof makeSkuFormSchema>>

export const getSkuFormDefaults = (sku?: ServiceSku): SkuFormData => ({
  active: sku?.active ?? true,
  cost: sku ? String(sku.cost) : '',
  duration: sku?.duration ? String(sku.duration) : '',
  name: sku?.name ?? '',
  quantity: String(sku?.quantity ?? 1),
})
